import { sidebarPage } from '../utils/sidebarPageLayout.js';
import { auth, db } from '../../src/firebase.js';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

export class Fund {
    constructor(containerId) { 
        this.containerId = containerId; 
        this.activeView = 'investor'; // investor or creator
        this.projects = [];
        this.isLoading = true;
        this.selectedCurrency = { code: 'EUR', symbol: '€', rate: 1 };
        this.currencies = [
            { code: 'EUR', symbol: '€', rate: 1 },
            { code: 'USD', symbol: '$', rate: 1.08 },
            { code: 'GBP', symbol: '£', rate: 0.86 },
            { code: 'JPY', symbol: '¥', rate: 165 },
            { code: 'AUD', symbol: 'A$', rate: 1.65 },
            { code: 'CAD', symbol: 'C$', rate: 1.48 },
            { code: 'CHF', symbol: 'Fr', rate: 0.98 },
            { code: 'CNY', symbol: '¥', rate: 7.82 },
            { code: 'HKD', symbol: 'HK$', rate: 8.45 },
            { code: 'NZD', symbol: 'NZ$', rate: 1.82 }
        ];
    }

    async init() {
        const el = document.getElementById(this.containerId);
        if (!el) return;

        // Auth Guard: Show bottom login modal if not authenticated
        if (!auth.currentUser) {
            this.injectStyles();
            el.innerHTML = `
                <div class="fv-auth-gate">
                    <div class="fv-auth-bottom-sheet fadeInSlideUp">
                        <div class="fv-auth-handle"></div>
                        <img src="/images/logo2.png" alt="Logo">
                        <h2>Sign in to continue</h2>
                        <p>Access the footballgirl fund to invest in projects or start your own.</p>
                        <button class="fv-btn-primary" id="fv-gate-login">Sign In / Join Now</button>
                        <button class="fg-btn" style="border:none; margin-top:10px;" id="fv-gate-cancel">Return Home</button>
                    </div>
                </div>
            `;
            el.querySelector('#fv-gate-login').onclick = () => window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'login' } }));
            el.querySelector('#fv-gate-cancel').onclick = () => window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'home' } }));
            return;
        }

        const user = auth.currentUser;
        const photoUrl = user?.photoURL || '/images/logo2.png';

        this.injectStyles();
        await this.fetchLiveProjects();

        const main = `
            <div class="fv-dashboard">
                <header class="fv-header">
                    <div class="fv-toggle-container">
                        <button id="fv-investor-btn" class="fv-toggle-btn ${this.activeView === 'investor' ? 'is-active' : ''}">Discover Projects</button>
                        <button id="fv-creator-btn" class="fv-toggle-btn ${this.activeView === 'creator' ? 'is-active' : ''}">My Studio</button>
                    </div>
                    <div class="fv-profile-wrap">
                        ${user ? `<button class="fg-dash-profile-trigger" id="fund-profile-btn"><img src="${photoUrl}" alt="Profile"></button>` : ''}
                    </div>
                </header>
                <section class="fv-main-content" id="fv-main-view" style="min-height: 450px;"></section>
                
                <div id="fv-modal-container"></div>
                <div class="fg-modal-overlay" id="fg-auth-guard-modal"></div>
            </div>
        `;

        el.innerHTML = sidebarPage({
            title: 'football<span class="fg-brand-girl">girl</span> fund',
            subtitle: 'Direct investment in community-led football initiatives.',
            main: main + `
                <!-- Fund Profile Drawer -->
                <div class="fg-dash-drawer-overlay" id="fund-drawer-overlay"></div>
                <div class="fg-dash-drawer" id="fund-profile-drawer">
                    <div class="fg-dash-drawer__header">
                        <h3>Account</h3>
                        <button id="fund-drawer-close">✕</button>
                    </div>
                    <div class="fg-dash-drawer__body">
                        <a href="#" data-page="creator-profile">👤 Profile</a>
                        <a href="#" data-page="creator-saved-projects">🔖 Saved projects</a>
                        <a href="#" data-page="curated-for-you">🎯 Recommended for you</a>
                        <a href="#" data-page="connections-feed">👥 Following</a>
                        <a href="#" data-page="account-controls">⚙️ Settings</a>
                        <a href="#" data-page="creator-inbox">✉️ Messages</a>
                        <a href="#" data-page="engagement-timeline">📊 Activity</a>
                        <a href="#" data-page="my-launches">🚀 Created projects</a>
                    </div>
                </div>
            `,
            wide: true
        });

        this.render(); // This will now correctly call the render method below
        this.attachEvents();
    }

    async fetchLiveProjects() {
        this.isLoading = true;
        try {
            const q = query(
                collection(db, 'projects'),
                where('status', '==', 'live'),
                where('public', '==', true)
            );
            const snap = await getDocs(q);
            this.projects = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("[Fund] Error fetching live projects:", error);
        } finally {
            this.isLoading = false;
        }
    }

    render() {
        const viewContainer = document.getElementById('fv-main-view');
        if (!viewContainer) return;

        viewContainer.innerHTML = this.activeView === 'investor' 
            ? this.renderInvestorView() 
            : this.renderCreatorView();
        
        this.attachDynamicEvents();
    }

    renderInvestorView() {
        if (this.isLoading) return '<div class="fv-loading-wrap"><div class="fg-loader"></div><p>Fetching active campaigns...</p></div>';
        if (this.projects.length === 0) return '<div class="fg-card" style="text-align:center;padding:4rem;"><h3 style="margin-bottom:10px;">No campaigns yet</h3><p>Be the first to support a creator. Check back soon!</p></div>';
        
        return `
            <div class="fv-project-grid">
                ${this.projects.map(p => this.renderProjectCard(p)).join('')}
            </div>
        `;
    }

    renderProjectCard(p) {
        const raised = p.raised || 0;
        const goal = p.fundingGoal || 1;
        const pct = Math.min(Math.round((raised / goal) * 100), 100);
        const rate = this.selectedCurrency.rate;
        const symbol = this.selectedCurrency.symbol;

        return `
            <article class="fv-project-card">
                <div class="fv-card-image" style="background-image: url('${p.imageURL || 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=800'}')"></div>
                <div class="fv-card-body">
                    <h3 class="fv-card-title">${p.title}</h3>
                    <div class="fv-card-meta">
                        <span><strong>Cause:</strong> ${p.category || 'Development'}</span>
                        <span><strong>Beneficiary:</strong> ${p.beneficiary || 'Local Community'}</span>
                    </div>
                    <div class="fv-progress-container">
                        <div class="fv-progress-bar"><div class="fv-progress-fill" style="width:${pct}%"></div></div>
                        <div class="fv-progress-labels">
                            <span>${symbol}${(raised * rate).toLocaleString()} raised</span>
                            <span>${pct}% of ${symbol}${(goal * rate).toLocaleString()}</span>
                        </div>
                    </div>
                    <button class="fv-donate-btn" data-id="${p.id}">Donate ${symbol}${(500 * rate).toLocaleString()}+</button>
                </div>
            </article>
        `;
    }

    renderCreatorView() {
        return `
            <div class="fv-studio-view fadeInSlideUp">
                <div class="fv-studio-card">
                    <div class="fv-studio-icon">🚀</div>
                    <h2>Start your project</h2>
                    <p>Tell your story, reach your goal, and empower your community.</p>
                    <button id="fv-open-create-modal" class="fv-btn-primary">Start Project</button>
                    <p style="font-size:0.75rem; color:#888; margin-top:20px;">95% of funds raised go directly to you. <br> Platform fee: 5%</p>
                </div>
            </div>
        `;
    }

    showModal() {
        const modalContainer = document.getElementById('fv-modal-container');
        modalContainer.innerHTML = `
            <div class="fv-modal-overlay">
                <div class="fv-modal-box">
                    <header class="fv-modal-header">
                        <h3>new <span class="fg-brand-football">football</span><span class="fg-brand-girl">girl</span> fund</h3>
                        <button id="fv-close-modal">✕</button>
                    </header>
                    <form id="fv-create-project-form" class="fv-form">
                        <div class="fv-form-field">
                            <label>Project Title</label>
                            <input type="text" name="title" required placeholder="Name your project">
                        </div>
                        <div class="fv-form-field">
                            <label>Funding Goal (€)</label>
                            <input type="number" id="fv-input-goal" name="goal" required placeholder="Min. 500">
                        </div>
                        <div class="fv-form-field">
                            <label>Beneficiary</label>
                            <input type="text" id="fv-input-ben" name="beneficiary" required placeholder="Who are you raising funds for?">
                        </div>
                        <div class="fv-form-field">
                            <label>Campaign Duration</label>
                            <select name="duration" id="fv-input-dur" required>
                                <option value="30">30 Days</option>
                                <option value="45">45 Days</option>
                                <option value="60">60 Days</option>
                            </select>
                        </div>
                        <div class="fv-fee-notice">
                            <strong>Platform Fee:</strong> 5% of total raised. <br>
                            <em>You keep 95% of all project funds.</em>
                        </div>
                        <button type="submit" id="fv-submit-draft" class="fv-btn-submit">Create Project Draft</button>
                    </form>
                </div>
            </div>
        `;

        const form = modalContainer.querySelector('#fv-create-project-form');
        const submitBtn = modalContainer.querySelector('#fv-submit-draft');

        const validate = () => {
            const goal = parseFloat(form.elements.goal.value) || 0;
            const title = form.elements.title.value.trim();
            const beneficiary = form.elements.beneficiary.value.trim();
            
            const isValid = title.length >= 3 && 
                            goal >= 500 && 
                            beneficiary.length >= 3;
            
            submitBtn.classList.toggle('is-disabled', !isValid);
        };

        form.addEventListener('input', validate);
        validate();

        form.onsubmit = async (e) => {
            e.preventDefault();
            const goal = parseFloat(form.elements.goal.value) || 0;
            const title = form.elements.title.value.trim();
            const beneficiary = form.elements.beneficiary.value.trim();
            
            if (submitBtn.classList.contains('is-disabled')) {
                let errors = [];
                if (title.length < 3) errors.push("Project Title (minimum 3 characters)");
                if (goal < 500) errors.push(`Funding Goal (minimum ${this.selectedCurrency.symbol}${(500 * this.selectedCurrency.rate).toLocaleString()})`);
                if (beneficiary.length < 3) errors.push("Beneficiary Name (minimum 3 characters)");
                
                alert("Please correct the following to continue:\n\n• " + errors.join("\n• "));
                return;
            }

            const formData = new FormData(form);
            const payload = {
                title: formData.get('title'),
                fundingGoal: Number(formData.get('goal')),
                beneficiary: formData.get('beneficiary'),
                duration: Number(formData.get('duration')),
                platformFeePercent: 5,
                status: 'draft',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            try {
                const user = auth.currentUser;
                await addDoc(collection(db, 'creators', user.uid, 'projects'), payload);
                modalContainer.innerHTML = '';
                alert("Project draft created successfully!");
                this.activeView = 'creator';
                this.render();
            } catch (err) { alert("Failed to save project. Check your connection."); }
        };

        modalContainer.querySelector('#fv-close-modal').onclick = () => { modalContainer.innerHTML = ''; };
    }

    injectStyles() {
        const styleId = 'fv-custom-styles';
        if (document.getElementById(styleId)) return;
        const css = `
            .fv-auth-gate { position: fixed; inset: 0; background: rgba(255,255,255,0.4); backdrop-filter: blur(15px); z-index: 2000; display: flex; align-items: flex-end; }
            .fv-auth-bottom-sheet { background: #fff; width: 100%; padding: 48px; border-radius: 32px 32px 0 0; text-align: center; box-shadow: 0 -10px 40px rgba(0,0,0,0.1); }
            .fv-auth-handle { width: 40px; height: 4px; background: #eee; border-radius: 2px; margin: -20px auto 30px; }
            .fv-auth-bottom-sheet img { width: 80px; margin-bottom: 20px; }
            .fv-auth-bottom-sheet h2 { font-weight: 800; margin-bottom: 10px; }

            .fv-dashboard { padding: 24px 0; text-align: left; }
            .fv-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 48px; }
            .fv-toggle-container { display: flex; background: var(--bg-secondary, #f0f2f5); padding: 4px; border-radius: 99px; border: 1px solid var(--border-color); }
            .fv-toggle-btn { padding: 10px 24px; border-radius: 99px; border: none; background: transparent; font-weight: 700; color: var(--text-secondary); cursor: pointer; transition: 0.2s; font-size: 0.85rem; }
            .fv-toggle-btn.is-active { background: #fff; color: var(--primary-blue); box-shadow: var(--shadow-sm); }

            .fv-currency-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding: 0 4px; }
            .fv-currency-wrap { position: relative; }
            .fv-currency-trigger { background: #fff; border: 1.5px solid var(--border-color); padding: 8px 16px; border-radius: 99px; cursor: pointer; font-size: 0.85rem; display: flex; gap: 8px; transition: 0.2s; }
            .fv-currency-trigger.active { border-color: var(--primary-blue); box-shadow: var(--shadow-sm); }
            .fv-currency-drawer { position: absolute; top: calc(100% + 8px); left: 0; background: #fff; border: 1px solid var(--border-color); border-radius: 16px; box-shadow: var(--shadow-lg); z-index: 100; min-width: 160px; overflow: hidden; display: none; }
            .fv-currency-drawer.is-open { display: block; animation: fadeIn 0.2s ease; }
            .fv-currency-opt { width: 100%; padding: 10px 16px; display: flex; justify-content: space-between; background: none; border: none; font-weight: 600; cursor: pointer; border-bottom: 1px solid #f8f9fa; }
            .fv-currency-opt:hover { background: #f0f7ff; color: var(--primary-blue); }
            .fv-min-notice { font-size: 0.75rem; font-weight: 800; color: #b91c1c; text-transform: uppercase; letter-spacing: 0.5px; }

            .fv-project-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; }
            .fv-project-card { background: #fff; border-radius: 16px; border: 1px solid var(--border-color); overflow: hidden; display: flex; flex-direction: column; transition: transform 0.2s; }
            .fv-project-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
            .fv-card-image { height: 180px; background-size: cover; background-position: center; }
            .fv-card-body { padding: 24px; flex: 1; display: flex; flex-direction: column; }
            .fv-card-title { font-size: 1.15rem; font-weight: 800; margin-bottom: 12px; line-height: 1.3; color: var(--text-primary); }
            .fv-card-meta { display: flex; flex-direction: column; gap: 4px; margin-bottom: 16px; font-size: 0.85rem; color: var(--text-secondary); }
            .fv-progress-container { margin-bottom: 24px; }
            .fv-progress-bar { height: 8px; background: var(--bg-secondary); border-radius: 4px; overflow: hidden; margin-bottom: 8px; }
            .fv-progress-fill { height: 100%; background: #22c55e; border-radius: 4px; }
            .fv-progress-labels { display: flex; justify-content: space-between; font-size: 0.75rem; font-weight: 700; color: var(--text-secondary); }
            .fv-donate-btn { width: 100%; padding: 14px; background: var(--primary-blue); color: #fff; border: none; border-radius: 12px; font-weight: 800; cursor: pointer; }
            .fv-studio-view { display: flex; justify-content: center; padding: 40px 0; }
            .fv-studio-card { max-width: 400px; text-align: center; background: #fff; padding: 48px; border-radius: 24px; border: 1px solid var(--border-color); }
            .fv-studio-icon { font-size: 3rem; margin-bottom: 20px; }
            .fv-btn-primary { padding: 16px 40px; background: #000; color: #fff; border-radius: 99px; border: none; font-weight: 800; cursor: pointer; }
            .fv-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(8px); z-index: 20000; display: flex; align-items: center; justify-content: center; padding: 20px; }
            .fv-modal-box { background: #fff; width: 100%; max-width: 480px; border-radius: 24px; padding: 32px; box-shadow: var(--shadow-lg); }
            .fv-modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
            .fv-modal-header h3 { margin: 0; font-weight: 800; }
            #fv-close-modal { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #999; }
            .fv-form-field { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
            .fv-form-field label { font-size: 0.75rem; font-weight: 800; text-transform: uppercase; color: var(--text-secondary); }
            .fv-form-field input, .fv-form-field select { padding: 12px; border: 1.5px solid var(--border-color); border-radius: 8px; font-family: inherit; font-size: 1rem; }
            .fv-fee-notice { padding: 12px; background: #fef2f2; border: 1px solid #fee2e2; border-radius: 8px; font-size: 0.8rem; color: #b91c1c; margin-bottom: 16px; line-height: 1.4; }
            .fv-btn-submit { width: 100%; padding: 16px; background: var(--primary-blue); color: #fff; border: none; border-radius: 12px; font-weight: 800; margin-top: 12px; cursor: pointer; }
            @media (max-width: 1024px) { .fv-project-grid { grid-template-columns: repeat(2, 1fr); } }
            @media (max-width: 768px) { .fv-project-grid { grid-template-columns: 1fr; } }

            .fg-dash-profile-trigger { width: 32px; height: 32px; border-radius: 50%; border: 1px solid var(--border-color); padding: 0; overflow: hidden; cursor: pointer; background: var(--bg-secondary); }
            .fg-dash-profile-trigger img { width: 100%; height: 100%; object-fit: cover; }
            .fg-dash-drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 10000; display: none; opacity: 0; transition: opacity 0.3s; }
            .fg-dash-drawer-overlay.is-visible { display: block; opacity: 1; }
            .fg-dash-drawer { position: fixed; top: 0; right: 0; width: 280px; height: 100%; background: #fff; z-index: 10001; transform: translateX(100%); transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); box-shadow: -4px 0 12px rgba(0,0,0,0.1); display: flex; flex-direction: column; text-align: left; }
            .fg-dash-drawer.is-open { transform: translateX(0); }
            .fg-dash-drawer__header { padding: 20px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--border-color); }
            .fg-dash-drawer__header h3 { margin: 0; font-size: 1rem; font-weight: 700; }
            #fund-drawer-close { background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--text-secondary); }
            .fg-dash-drawer__body { padding: 10px 0; overflow-y: auto; }
            .fg-dash-drawer__body a { display: block; padding: 12px 20px; text-decoration: none; color: var(--text-primary); font-size: 0.95rem; font-weight: 500; transition: background 0.2s; }
            .fg-dash-drawer__body a:hover { background: var(--bg-secondary); }

            @media (max-width: 768px) {
                .fg-dash-drawer { width: 100%; top: auto; bottom: 0; height: auto; border-radius: 20px 20px 0 0; transform: translateY(100%); }
                .fg-dash-drawer.is-open { transform: translateY(0); }
            }

            [data-theme="dark"] .fg-dash-drawer { background: #18191a; }
            [data-theme="dark"] .fg-dash-drawer__body a:hover { background: #2a2b2d; }
        `;
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = css;
        document.head.appendChild(style);
    }

    attachEvents() {
        const el = document.getElementById(this.containerId);
        if (!el) return;

        // Tab Switching
        el.querySelector('#fv-investor-btn')?.addEventListener('click', (e) => { 
            this.activeView = 'investor'; 
            el.querySelectorAll('.fv-toggle-btn').forEach(b => b.classList.remove('is-active'));
            e.target.classList.add('is-active');
            this.render(); 
        });

        el.querySelector('#fv-creator-btn')?.addEventListener('click', (e) => { 
            this.activeView = 'creator'; 
            el.querySelectorAll('.fv-toggle-btn').forEach(b => b.classList.remove('is-active'));
            e.target.classList.add('is-active');
            this.render(); 
        });

        // Profile Drawer
        const drawerTrigger = el.querySelector('#fund-profile-btn');
        const drawer = el.querySelector('#fund-profile-drawer');
        const drawerOverlay = el.querySelector('#fund-drawer-overlay');
        const closeBtn = el.querySelector('#fund-drawer-close');

        const toggleDrawer = () => {
            drawer?.classList.toggle('is-open');
            drawerOverlay?.classList.toggle('is-visible');
        };

        drawerTrigger?.addEventListener('click', toggleDrawer);
        [drawerOverlay, closeBtn].forEach(node => node?.addEventListener('click', toggleDrawer));

        drawer?.addEventListener('click', (e) => {
            const link = e.target.closest('a[data-page]');
            if (!link) return;
            toggleDrawer();
            window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: link.dataset.page } }));
        });
    }

    attachDynamicEvents() {
        const el = document.getElementById(this.containerId);
        if (!el) return;

        // Currency Logic
        const curTrigger = el.querySelector('#fv-currency-trigger');
        const curDrawer = el.querySelector('#fv-currency-drawer');

        if (curTrigger) {
            curTrigger.onclick = (e) => {
                e.stopPropagation();
                curDrawer.classList.toggle('is-open');
                curTrigger.classList.toggle('active');
            };

            document.addEventListener('click', () => {
                curDrawer?.classList.remove('is-open');
                curTrigger?.classList.remove('active');
            });

            el.querySelectorAll('.fv-currency-opt').forEach(opt => {
                opt.onclick = () => {
                    const code = opt.dataset.code;
                    this.selectedCurrency = this.currencies.find(c => c.code === code);
                    this.render();
                };
            });
        }

        // Donation and Modal triggers
        el.querySelectorAll('.fv-donate-btn').forEach(btn => {
            btn.onclick = async () => {
                if (!auth.currentUser) {
                    window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'login' } }));
                    return;
                }
                btn.disabled = true;
                btn.textContent = "Connecting...";
                try {
                    const functions = getFunctions();
                    const createDonationSession = httpsCallable(functions, 'createDonationSession');
                    const { data } = await createDonationSession({ 
                        projectId: btn.dataset.id,
                        success_url: window.location.origin + '/#page=fund&status=success',
                        cancel_url: window.location.href
                    });
                    if (data?.url) window.location.assign(data.url);
                } catch (e) {
                    alert("Unable to process donation. Please try again later.");
                    btn.disabled = false;
                    btn.textContent = "Donate";
                }
            };
        });

        el.querySelector('#fv-open-create-modal')?.addEventListener('click', () => {
            if (!auth.currentUser) {
                window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'login' } }));
                return;
            }
            this.showModal();
        });
    }
}